import { useEffect, useMemo, useRef, useState } from 'react';
import type { LayoutTemplateSlot } from '@/lib/api';
import { applyPhotosToTemplateSvg, type PhotoFit } from '@/lib/templatePipeline';

/**
 * Parse the viewBox width/height from an SVG string. Returns null if absent
 * or unparseable. Used to compute the cover-fit projection so the
 * interaction overlay aligns with the visible SVG after object-fit:cover.
 */
export function parseSvgViewBoxSize(svgString: string): { w: number; h: number } | null {
  // viewBox="x y w h" on the root <svg>.
  const vb = svgString.match(
    /<svg\b[^>]*\sviewBox\s*=\s*["']\s*([\d.eE+-]+)\s+([\d.eE+-]+)\s+([\d.eE+-]+)\s+([\d.eE+-]+)\s*["']/i,
  );
  if (vb) {
    const w = parseFloat(vb[3]);
    const h = parseFloat(vb[4]);
    if (w > 0 && h > 0) return { w, h };
  }
  // Fallback: width + height attrs on the root <svg> (px assumed).
  const w = svgString.match(/<svg\b[^>]*\swidth\s*=\s*["']?([\d.]+)/i);
  const h = svgString.match(/<svg\b[^>]*\sheight\s*=\s*["']?([\d.]+)/i);
  if (w && h) {
    const wv = parseFloat(w[1]);
    const hv = parseFloat(h[1]);
    if (wv > 0 && hv > 0) return { w: wv, h: hv };
  }
  return null;
}

/**
 * Project a slot bbox (0..1 of SVG viewBox) into container-local % coords
 * after `object-fit: cover` is applied to the SVG. This is the same math
 * the browser uses to lay out the <img>:
 *
 *   coverScale = max(containerW / viewBoxW, containerH / viewBoxH)
 *   renderedW  = viewBoxW * coverScale  (≥ containerW after cover)
 *   renderedH  = viewBoxH * coverScale
 *   offsetX    = (containerW - renderedW) / 2   (≤ 0, centred)
 *   offsetY    = (containerH - renderedH) / 2
 *
 *   slot_container_x_px = slot.x * viewBoxW * coverScale + offsetX
 *   slot_container_x_pct = slot_container_x_px / containerW * 100
 *
 * When viewBox aspect matches the container, coverScale collapses to a
 * single ratio and offsets are zero, so the result is just slot.x * 100
 * (= the original behavior). Pure function; no DOM access.
 */
export function projectSlotToContainerPct(
  slot: { x: number; y: number; width: number; height: number },
  viewBox: { w: number; h: number },
  container: { w: number; h: number },
): { left: number; top: number; width: number; height: number } {
  if (viewBox.w <= 0 || viewBox.h <= 0 || container.w <= 0 || container.h <= 0) {
    return { left: slot.x * 100, top: slot.y * 100, width: slot.width * 100, height: slot.height * 100 };
  }
  const coverScale = Math.max(container.w / viewBox.w, container.h / viewBox.h);
  const renderedW = viewBox.w * coverScale;
  const renderedH = viewBox.h * coverScale;
  const offsetX = (container.w - renderedW) / 2;
  const offsetY = (container.h - renderedH) / 2;
  return {
    left:   (slot.x      * viewBox.w * coverScale + offsetX) / container.w * 100,
    top:    (slot.y      * viewBox.h * coverScale + offsetY) / container.h * 100,
    width:  (slot.width  * viewBox.w * coverScale)           / container.w * 100,
    height: (slot.height * viewBox.h * coverScale)           / container.h * 100,
  };
}

interface TemplatedPageProps {
  /** Original SVG fetched from R2 (cached upstream). */
  svgString: string;
  /** Template's slot definitions (only dom_index is load-bearing for rendering). */
  slots: LayoutTemplateSlot[];
  /** Customer photo URL per slot — keyed by SLOT INDEX (not dom_index) so
   *  callers can use their existing index-based state without rekeying. */
  photoUrlsBySlotIndex: Record<number, string | null>;
  photoFitsBySlotIndex?: Record<number, PhotoFit | undefined>;
  pageWidthPx: number;
  pageHeightPx: number;

  /** Editor only — interactive overlay layer. */
  interactive?: boolean;
  selectedSlotIndex?: number | null;
  onSlotSelect?: (slotIndex: number) => void;
  /** Called when admin drops a photo onto a slot. `photoId` + `source` mirror
   *  the existing PhotoSlot drag protocol so the photo-strip works unchanged. */
  onSlotDrop?: (slotIndex: number, photoId: string, source: 'slot' | 'sidebar') => void;
  onFitChange?: (slotIndex: number, fit: PhotoFit) => void;
  photoIdBySlotIndex?: Record<number, string | null>;
}

export function TemplatedPage({
  svgString,
  slots,
  photoUrlsBySlotIndex,
  photoFitsBySlotIndex,
  pageWidthPx,
  pageHeightPx,
  interactive = false,
  selectedSlotIndex = null,
  onSlotSelect,
  onSlotDrop,
  onFitChange,
  photoIdBySlotIndex,
}: TemplatedPageProps) {
  // Build the dom_index-keyed maps from the slot-index-keyed inputs once
  // per render. Slots without a dom_index are skipped — upstream should
  // have fallen back to TemplatedPhotoSlot for legacy templates.
  const { photoUrlsByDomIndex, photoFitsByDomIndex, slotsArg } = useMemo(() => {
    const urls: Record<number, string> = {};
    const fits: Record<number, PhotoFit> = {};
    const args: Array<{ dom_index: number }> = [];
    for (let i = 0; i < slots.length; i++) {
      const slot = slots[i];
      if (typeof slot.dom_index !== 'number') continue;
      args.push({ dom_index: slot.dom_index });
      const url = photoUrlsBySlotIndex[i];
      if (url) urls[slot.dom_index] = url;
      const fit = photoFitsBySlotIndex?.[i];
      if (fit) fits[slot.dom_index] = fit;
    }
    return { photoUrlsByDomIndex: urls, photoFitsByDomIndex: fits, slotsArg: args };
  }, [slots, photoUrlsBySlotIndex, photoFitsBySlotIndex]);

  // Parse the SVG's viewBox once per svgString. Used by the overlay layer
  // below to project slot coords through the same cover-fit transform the
  // <img> uses, so drag/drop hit-test aligns with the visible slot even
  // when viewBox aspect doesn't match the A4 page container.
  const viewBox = useMemo(() => parseSvgViewBoxSize(svgString), [svgString]);

  // SVG-in-img security: when an SVG is loaded via <img src=>, browsers
  // BLOCK any external resource the SVG tries to fetch. We work around
  // that by converting each customer photo to a base64 data URL and
  // embedding it in the SVG before generating the blob URL. Inert
  // payload — no cross-origin fetch needed at render time.
  //
  // CRITICAL: build a CONTENT-BASED dep key for the useEffect. The
  // photoUrlsByDomIndex / photoFitsByDomIndex / slotsArg objects are
  // memoized but React still compares them by reference, and even one
  // unrelated parent render that produces a new slots[] array would
  // re-run this effect. Re-running it triggers a fetch + blob URL swap +
  // image remount, which makes the editor flicker on every hover, click,
  // and drag. We compare by string content instead so the effect only
  // re-runs when something the SVG actually depends on changed.
  const effectKey = useMemo(() => {
    // svgString is huge; use length + first-100-chars as a fingerprint.
    // Collisions are vanishingly unlikely for our use case (one SVG per
    // template, persisted to R2).
    const svgFingerprint = `${svgString.length}:${svgString.slice(0, 100)}`;
    const urlEntries = Object.keys(photoUrlsByDomIndex)
      .map((k) => Number(k))
      .sort((a, b) => a - b)
      .map((k) => `${k}=${photoUrlsByDomIndex[k]}`)
      .join('|');
    const fitEntries = Object.keys(photoFitsByDomIndex)
      .map((k) => Number(k))
      .sort((a, b) => a - b)
      .map((k) => {
        const f = photoFitsByDomIndex[k];
        return f ? `${k}=${f.scale},${f.offsetX},${f.offsetY}` : `${k}=`;
      })
      .join('|');
    const slotsKey = slotsArg.map((s) => s.dom_index).sort((a, b) => a - b).join(',');
    return `${svgFingerprint}||${urlEntries}||${fitEntries}||${slotsKey}`;
  }, [svgString, photoUrlsByDomIndex, photoFitsByDomIndex, slotsArg]);

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  // Refs to the latest values — used inside the effect WITHOUT including
  // in deps, so the effect's dependency array is just `effectKey`.
  const latestArgsRef = useRef({ svgString, photoUrlsByDomIndex, photoFitsByDomIndex, slotsArg });
  latestArgsRef.current = { svgString, photoUrlsByDomIndex, photoFitsByDomIndex, slotsArg };

  useEffect(() => {
    let cancelled = false;
    let currentBlobUrl: string | null = null;
    const args = latestArgsRef.current;

    (async () => {
      const entries = Object.entries(args.photoUrlsByDomIndex);
      const dataUrlPairs = await Promise.all(
        entries.map(async ([di, url]) => {
          try {
            const dataUrl = await fetchPhotoAsDataUrl(url);
            return [Number(di), dataUrl] as const;
          } catch {
            return [Number(di), null] as const;
          }
        }),
      );
      if (cancelled) return;

      const resolvedUrls: Record<number, string> = {};
      for (const [di, dataUrl] of dataUrlPairs) {
        if (dataUrl) resolvedUrls[di] = dataUrl;
      }

      const modifiedSvg = applyPhotosToTemplateSvg(
        args.svgString,
        resolvedUrls,
        args.photoFitsByDomIndex,
        args.slotsArg,
      );
      const blob = new Blob([modifiedSvg], { type: 'image/svg+xml' });
      currentBlobUrl = URL.createObjectURL(blob);
      if (cancelled) {
        URL.revokeObjectURL(currentBlobUrl);
        return;
      }
      setBlobUrl((prev) => {
        // Revoke the OLD blob URL only after we've swapped to the new one.
        // Doing it inside the next setBlobUrl call avoids the React-double-
        // invoke effect-cleanup gotcha where we'd revoke the URL that's
        // still being shown.
        if (prev) URL.revokeObjectURL(prev);
        return currentBlobUrl;
      });
    })();

    return () => {
      cancelled = true;
      // Don't revoke currentBlobUrl here — it might be the URL we just
      // committed. The next setBlobUrl callback revokes the previous one.
    };
  }, [effectKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Revoke the final blob URL on unmount so we don't leak.
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  // Capture blobUrl at unmount time via the cleanup closure — refresh on
  // each blobUrl change so the unmount handler sees the latest.
  }, [blobUrl]);

  // Image load tracking. Critically: DON'T reset imgLoaded on every blobUrl
  // change — the new image will swap in INSTANTLY (browser caches the
  // visual), and forcing opacity:0 → onLoad → opacity:1 just produces a
  // flicker. The browser already handles src changes smoothly via its own
  // image cache; trust it.
  const [imgLoaded, setImgLoaded] = useState(false);

  // Count empty slots for the badge — the prompt for the customer to fill
  // the remaining frames.
  const emptySlotCount = useMemo(() => {
    let n = 0;
    for (let i = 0; i < slots.length; i++) {
      if (!photoUrlsBySlotIndex[i]) n++;
    }
    return n;
  }, [slots, photoUrlsBySlotIndex]);

  // Compute the empty-slot number (1-based) for each slot so the affordance
  // badge can show "Photo 2/5" instead of a vague "Glissez une photo ici".
  const emptySlotOrder = useMemo(() => {
    const order: Record<number, number> = {};
    let n = 0;
    for (let i = 0; i < slots.length; i++) {
      if (!photoUrlsBySlotIndex[i]) {
        n++;
        order[i] = n;
      }
    }
    return order;
  }, [slots, photoUrlsBySlotIndex]);

  return (
    // Admin night mode: a light island — an album page is never re-coloured (index.css › ADMIN NIGHT MODE).
    <div data-adm-media="" style={{ position: 'relative', width: pageWidthPx, height: pageHeightPx, overflow: 'hidden' }}>
      {/* Visual layer — modified SVG via blob-URL <img>. Native SVG render
          path: fast, off-thread, doesn't touch React's DOM.
          object-fit: cover so templates whose viewBox isn't a perfect A4
          match (or that have whitespace baked into the SVG) scale up to
          fill the page rather than letterboxing with cream margins. */}
      {blobUrl && (
        <img
          src={blobUrl}
          alt=""
          draggable={false}
          onLoad={() => setImgLoaded(true)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            // Fade in on FIRST load only. Once imgLoaded is true, every
            // subsequent src swap stays at opacity 1 — the browser swaps
            // visually instant for cached blob URLs and any further
            // opacity:0 → fade-in would just produce flicker on every
            // photo drop / state change.
            opacity: imgLoaded ? 1 : 0,
            transition: 'opacity 200ms ease-out',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Loading skeleton — shows until the <img> onLoad fires. Subtle
          shimmer so the customer sees something is happening on slow
          renders. */}
      {!imgLoaded && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(110deg, #f4f0e8 30%, #ebe5d8 50%, #f4f0e8 70%)',
            backgroundSize: '200% 100%',
            animation: 'templated-page-shimmer 1.4s ease-in-out infinite',
          }}
        />
      )}

      {/* Counter pill — top-right, persistent reminder of how many slots
          still need a photo. Only visible when there are empties. */}
      {interactive && emptySlotCount > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            background: 'rgba(201, 162, 74, 0.95)',
            color: '#fff',
            padding: '4px 10px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 600,
            zIndex: 20,
            pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          {emptySlotCount === 1 ? '1 photo à ajouter' : `${emptySlotCount} photos à ajouter`}
        </div>
      )}

      {/* Inline keyframe for the shimmer — no global stylesheet dep. */}
      <style>{`
        @keyframes templated-page-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      {/* Interaction layer — one transparent positioned div per slot.
          Slot coords are 0..1 of the SVG viewBox; we project them through
          the same object-fit:cover transform the <img> uses so the
          overlay hit-test aligns with the visible slot for non-A4
          templates. When viewBox is unknown we fall back to the original
          slot-coord behavior (no projection). */}
      {interactive && slots.map((slot, i) => {
        const projected = viewBox
          ? projectSlotToContainerPct(slot, viewBox, { w: pageWidthPx, h: pageHeightPx })
          : { left: slot.x * 100, top: slot.y * 100, width: slot.width * 100, height: slot.height * 100 };
        return (
          <SlotInteractionOverlay
            key={i}
            slotIndex={i}
            slot={slot}
            projected={projected}
            isSelected={selectedSlotIndex === i}
            hasPhoto={!!photoUrlsBySlotIndex[i]}
            emptySlotNumber={emptySlotOrder[i]}
            fit={photoFitsBySlotIndex?.[i]}
            photoId={photoIdBySlotIndex?.[i] ?? null}
            slotWidthPx={(projected.width / 100) * pageWidthPx}
            slotHeightPx={(projected.height / 100) * pageHeightPx}
            onSelect={onSlotSelect}
            onDrop={onSlotDrop}
            onFitChange={onFitChange}
          />
        );
      })}
    </div>
  );
}

interface OverlayProps {
  slotIndex: number;
  slot: LayoutTemplateSlot;
  /** Container-local %-coords AFTER the SVG's cover-fit projection. The
   *  overlay div uses these instead of slot.x/y/width/height so the
   *  hit-test follows the visible slot when viewBox aspect differs from
   *  the A4 page container. */
  projected: { left: number; top: number; width: number; height: number };
  isSelected: boolean;
  hasPhoto: boolean;
  /** 1-based ordinal for empty slots ("Photo 2"). undefined for filled. */
  emptySlotNumber?: number;
  fit: PhotoFit | undefined;
  photoId: string | null;
  slotWidthPx: number;
  slotHeightPx: number;
  onSelect?: (slotIndex: number) => void;
  onDrop?: (slotIndex: number, photoId: string, source: 'slot' | 'sidebar') => void;
  onFitChange?: (slotIndex: number, fit: PhotoFit) => void;
}

function SlotInteractionOverlay({
  slotIndex,
  slot,
  projected,
  isSelected,
  hasPhoto,
  emptySlotNumber,
  fit,
  photoId,
  slotWidthPx,
  slotHeightPx,
  onSelect,
  onDrop,
  onFitChange,
}: OverlayProps) {
  const [isOver, setIsOver] = useState(false);

  const dragRef = useRef<{ startX: number; startY: number; origOffsetX: number; origOffsetY: number } | null>(null);
  const currentFit = fit ?? { scale: 1, offsetX: 0, offsetY: 0 };
  const onPointerDown = (e: React.MouseEvent) => {
    if (!isSelected || !hasPhoto || !onFitChange) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origOffsetX: currentFit.offsetX,
      origOffsetY: currentFit.offsetY,
    };
    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = ((ev.clientX - dragRef.current.startX) / slotWidthPx) * 100;
      const dy = ((ev.clientY - dragRef.current.startY) / slotHeightPx) * 100;
      onFitChange(slotIndex, {
        scale: currentFit.scale,
        offsetX: dragRef.current.origOffsetX + dx,
        offsetY: dragRef.current.origOffsetY + dy,
      });
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const dropHandlers = onDrop
    ? {
        onDragOver: (e: React.DragEvent) => { e.preventDefault(); if (!isOver) setIsOver(true); },
        onDragEnter: (e: React.DragEvent) => { e.preventDefault(); setIsOver(true); },
        onDragLeave: () => setIsOver(false),
        onDrop: (e: React.DragEvent) => {
          e.preventDefault();
          setIsOver(false);
          const pid = e.dataTransfer.getData('photoId');
          const src = (e.dataTransfer.getData('photoSource') as 'slot' | 'sidebar') || 'sidebar';
          if (pid && onDrop) onDrop(slotIndex, pid, src);
        },
      }
    : {};

  const onDragStart = onDrop && photoId
    ? (e: React.DragEvent) => {
        e.dataTransfer.setData('photoId', photoId);
        e.dataTransfer.setData('photoSource', 'slot');
        e.dataTransfer.effectAllowed = 'move';
      }
    : undefined;

  const emptyAffordance = !hasPhoto;
  const overBg = isOver ? 'rgba(201, 162, 74, 0.28)' : 'rgba(201, 162, 74, 0.10)';

  return (
    <div
      onClick={() => onSelect?.(slotIndex)}
      onMouseDown={onPointerDown}
      draggable={!!onDrop && !!photoId}
      onDragStart={onDragStart}
      {...dropHandlers}
      style={{
        position: 'absolute',
        left: `${projected.left}%`,
        top: `${projected.top}%`,
        width: `${projected.width}%`,
        height: `${projected.height}%`,
        transform: slot.rotation ? `rotate(${slot.rotation}deg)` : undefined,
        transformOrigin: 'center',
        outline: isSelected ? '2px solid #C9A24A' : undefined,
        outlineOffset: '-2px',
        background: emptyAffordance ? overBg : 'transparent',
        border: emptyAffordance ? `2px ${isOver ? 'solid' : 'dashed'} #C9A24A` : undefined,
        display: emptyAffordance ? 'flex' : undefined,
        alignItems: emptyAffordance ? 'center' : undefined,
        justifyContent: emptyAffordance ? 'center' : undefined,
        color: '#946815',
        fontSize: 12,
        fontWeight: 500,
        textAlign: 'center',
        padding: 8,
        boxSizing: 'border-box',
        cursor: hasPhoto && isSelected && onFitChange ? 'move' : 'pointer',
        zIndex: 10,
      }}
    >
      {emptyAffordance && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          {/* Big numbered badge — easy to scan across the page when multiple
              empty slots are visible. */}
          {typeof emptySlotNumber === 'number' && (
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: '#C9A24A',
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 4,
              }}
            >
              {emptySlotNumber}
            </div>
          )}
          <div>{isOver ? 'Déposer' : 'Glissez une photo ici'}</div>
        </div>
      )}
    </div>
  );
}

/** SVG cache shared across all TemplatedPageWithSvgFetch instances.
 *  Module-level Map keyed on URL — the same SVG is fetched once per session. */
const svgCache = new Map<string, Promise<string>>();

function fetchSvgCached(url: string): Promise<string> {
  if (!svgCache.has(url)) {
    svgCache.set(url, fetch(url).then((r) => r.text()));
  }
  return svgCache.get(url)!;
}

/** Photo data-URL cache. Customer photos are fetched once per signed URL,
 *  converted to base64, and the result is reused on every re-render. Stable
 *  for the editor session (Supabase signed URLs are ~1 h TTL). */
const photoDataUrlCache = new Map<string, Promise<string>>();

function fetchPhotoAsDataUrl(url: string): Promise<string> {
  if (!photoDataUrlCache.has(url)) {
    const p = (async () => {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`photo fetch ${res.status}`);
      const blob = await res.blob();
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
        reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
        reader.readAsDataURL(blob);
      });
    })();
    // Don't cache a rejected promise — let the next render retry.
    p.catch(() => photoDataUrlCache.delete(url));
    photoDataUrlCache.set(url, p);
  }
  return photoDataUrlCache.get(url)!;
}

interface TemplatedPageWithSvgFetchProps extends Omit<TemplatedPageProps, 'svgString'> {
  /** Public R2 URL for the template's original SVG. */
  svgUrl: string;
}

/**
 * Convenience wrapper that fetches the SVG (cached) before rendering
 * TemplatedPage. Returns a neutral placeholder while loading.
 */
export function TemplatedPageWithSvgFetch({
  svgUrl,
  pageWidthPx,
  pageHeightPx,
  ...rest
}: TemplatedPageWithSvgFetchProps) {
  const [svgString, setSvgString] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchSvgCached(svgUrl).then((s) => {
      if (!cancelled) setSvgString(s);
    });
    return () => { cancelled = true; };
  }, [svgUrl]);

  if (!svgString) {
    return (
      <div
        style={{
          width: pageWidthPx,
          height: pageHeightPx,
          background: '#f4f4f4',
        }}
      />
    );
  }
  return (
    <TemplatedPage
      svgString={svgString}
      pageWidthPx={pageWidthPx}
      pageHeightPx={pageHeightPx}
      {...rest}
    />
  );
}
